import { InformationRowContainer } from "@/components/lazo-one-settings-components/profile-components/CompanyInformation";
import { InformationFormContainer } from "@/components/lazo-one-settings-components/profile-components/PersonalInformationEditForm";
import { FINAL_CATEGORIES, INDUSTRIES, NUMBER_OF_EMPLOYESS, YEARS } from "@/components/onboarding-components/OnboardingCompanyForm";
import CommonButton from "@/components/utils/CommonButton";
import CommonFormControl from "@/components/utils/CommonFormControl";
import CountrySelect from "@/components/utils/CountrySelect";
import LazoModal from "@/components/utils/LazoModal";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import StatesSelect from "@/components/utils/StatesSelect";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoCompany } from "@/models/LazoCompany";
import { CountryData, StateData } from "@/services/countries/CountriesService";
import LazoCompanyService from "@/services/LazoCompanyService";
import { ExpandMore } from "@mui/icons-material";
import { FormLabel, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Box } from "@mui/system";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type CompanyEditFormProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  company: LazoCompany;
  setCompany: Dispatch<SetStateAction<LazoCompany>>;
};

function mapToInstance(data, company: LazoCompany): LazoCompany {
  return {
    ...company,
    externalId: company.externalId,
    address: {
      externalId: company.address.externalId,
      streetAddress: data.streetAddress,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
    },
    tenant: {
      ...company.tenant,
      name: data.companyName,
    },
    companyContext: {
      cashOnHand: data.cashOnHand,
      monthlyRevenue: data.monthlyRevenue,
      totalFundingReceived: data.totalFundingReceived,
      ebitda: data.ebitda,
      companyDescription: data.companyDescription,
      twitter: data.twitter,
      industry: data.industry,
      instagram: data.instagram,
      companyName: data.companyName,
      linkedIn: data.linkedIn,
    },
    ...data,
  };
}

function CompanyEditForm(props: CompanyEditFormProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
  const [selectedState, setSelectedState] = useState<StateData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");

  const { triggerFetch } = useTenantMemberships();
  const { register, handleSubmit, control, setValue, getValues } = useForm();
  const [otherIndustry, setOtherIndustry] = useState<boolean>(false);

  function onSubmit(data) {
    setLoading(true);
    //this method is used for tasks. If in the future we reuse this form, we need to pass if it is from task.
    LazoCompanyService.updateCompanyOnTask(mapToInstance(data, props.company))
      .then((r) => {
        r.onSuccess((c) => {
          // Trigger fetch on tenant memberships when company name changes to change combobox
          if (data.companyName !== props.company.companyName) {
            triggerFetch();
          }
          props.setCompany(c);
          NotificationService.info(t("profile.companyInformation.notifications.updateSuccess"));
          props.setOpen(false);
        });
        r.onError((message, response) => NotificationService.error(message));
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (selectedState != null) setValue("state", selectedState.name);
  }, [selectedState]);

  return (
    <LazoModal
      title={t("profile.companyInformation.title")}
      open={props.open}
      handleClose={() => props.setOpen(false)}
      sx={{ width: "35rem" }}
      buttons={
        loading
          ? [
              <Box sx={{ width: "100%" }}>
                <Spinner sx={{ color: Colors.LAZO_PURPLE }} variant={"indeterminate"} />
              </Box>,
            ]
          : [
              <CommonButton className={"purple-border-variant"} style={{ borderRadius: "0.5rem" }} onClick={() => props.setOpen(false)}>
                {tCommon("cancel")}
              </CommonButton>,
              <CommonButton style={{ borderRadius: "0.5rem" }} onClick={handleSubmit(onSubmit)} type={"submit"}>
                {tCommon("save")}
              </CommonButton>,
            ]
      }
    >
      <InformationFormContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.companyName")}</FormLabel>
            <Controller
              control={control}
              name={"companyName"}
              defaultValue={props.company.companyName}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  {...register("companyName", { required: true })}
                  placeholder={error && t("profile.companyInformation.validation.companyNameRequired")}
                  onChange={onChange}
                  error={!!error}
                  value={value}
                />
              )}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.legalName")}</FormLabel>
            <Controller
              control={control}
              name={"legalName"}
              defaultValue={props.company.legalName}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  {...register("legalName", { required: true })}
                  placeholder={error && t("profile.companyInformation.validation.legalNameRequired")}
                  onChange={onChange}
                  error={!!error}
                  value={value}
                />
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CountrySelect
            register={register}
            selectedCountry={selectedCountry}
            control={control}
            setSelectedCountry={setSelectedCountry}
            formControlProps={{ sx: { flex: "0.5" } }}
            defaultValue={props.company.country}
          />
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.zipCode")}</FormLabel>
            <Controller
              control={control}
              name={"zipCode"}
              defaultValue={props.company?.address?.zipCode}
              render={({ field: { onChange, value } }) => (
                <TextField
                  {...register("zipCode", {
                    required: true,
                    valueAsNumber: true,
                  })}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer sx={{ gap: 1 }}>
          <StatesSelect
            formControlProps={{ sx: { flex: "0.5" } }}
            register={register}
            control={control}
            country={selectedCountry}
            selectedState={selectedState}
            setSelectedState={setSelectedState}
            defaultValue={props.company?.address?.state}
            required={true}
          />
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.city")}</FormLabel>
            <Controller
              control={control}
              name={"city"}
              defaultValue={props.company?.address?.city}
              render={({ field: { onChange, value } }) => <TextField {...register("city")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <CommonFormControl sx={{ flex: "1" }}>
          <FormLabel>{t("profile.companyInformation.fields.address")}</FormLabel>
          <Controller
            control={control}
            name={"streetAddress"}
            defaultValue={props.company?.address?.streetAddress}
            render={({ field: { onChange, value } }) => <TextField {...register("streetAddress")} onChange={onChange} value={value} />}
          />
        </CommonFormControl>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.website")}</FormLabel>
            <Controller
              control={control}
              name={"url"}
              defaultValue={props.company.url}
              render={({ field: { onChange, value } }) => <TextField {...register("url")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.telephone")}</FormLabel>
            <Controller
              control={control}
              name={"telephone"}
              defaultValue={props.company.telephone}
              render={({ field: { onChange, value } }) => (
                <TextField {...register("telephone", { required: true })} onChange={onChange} value={value} />
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.yearFounded")}</FormLabel>
            <Controller
              control={control}
              name={"yearFounded"}
              defaultValue={props.company.yearFounded}
              render={({ field: { value } }) => (
                <CommonStyledSelect
                  {...register("yearFounded")}
                  IconComponent={ExpandMore}
                  value={value}
                  MenuProps={CommonStyledSelectMenuProps}
                >
                  {YEARS.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </CommonStyledSelect>
              )}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.latestFinancialStage")}</FormLabel>
            <Controller
              control={control}
              name={"startupStage"}
              defaultValue={props.company.startupStage}
              render={({ field: { value } }) => (
                <CommonStyledSelect
                  {...register("startupStage")}
                  IconComponent={ExpandMore}
                  value={value}
                  MenuProps={CommonStyledSelectMenuProps}
                >
                  {FINAL_CATEGORIES.sort().map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      {stage}
                    </MenuItem>
                  ))}
                </CommonStyledSelect>
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.numberOfEmployees")}</FormLabel>
            <Controller
              control={control}
              name={"numberOfEmployees"}
              defaultValue={props.company.numberOfEmployees}
              render={({ field: { value } }) => (
                <CommonStyledSelect
                  {...register("numberOfEmployees")}
                  IconComponent={ExpandMore}
                  value={value}
                  MenuProps={CommonStyledSelectMenuProps}
                >
                  {NUMBER_OF_EMPLOYESS.map((number) => (
                    <MenuItem key={number} value={number}>
                      {number}
                    </MenuItem>
                  ))}
                </CommonStyledSelect>
              )}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.keyInvestors")}</FormLabel>
            <Controller
              control={control}
              name={"keyInvestors"}
              defaultValue={props.company.keyInvestors}
              render={({ field: { onChange, value } }) => <TextField {...register("keyInvestors")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.ebitda")}</FormLabel>
            <Controller
              control={control}
              name={"ebitda"}
              defaultValue={props.company.companyContext?.ebitda}
              render={({ field: { onChange, value } }) => <TextField {...register("ebitda")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.cashOnHand")}</FormLabel>
            <Controller
              control={control}
              name={"cashOnHand"}
              defaultValue={props.company.companyContext?.cashOnHand}
              render={({ field: { onChange, value } }) => <TextField {...register("cashOnHand")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.monthlyRevenue")}</FormLabel>
            <Controller
              control={control}
              name={"monthlyRevenue"}
              defaultValue={props.company.companyContext?.monthlyRevenue}
              render={({ field: { onChange, value } }) => <TextField {...register("monthlyRevenue")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.totalFundingReceived")}</FormLabel>
            <Controller
              control={control}
              name={"totalFundingReceived"}
              defaultValue={props.company.companyContext?.totalFundingReceived}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField {...register("totalFundingReceived")} onChange={onChange} error={!!error} value={value} />
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={1}>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.instagram")}</FormLabel>
            <Controller
              control={control}
              name={"instagram"}
              defaultValue={props.company.companyContext?.instagram}
              render={({ field: { onChange, value } }) => <TextField {...register("instagram")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.linkedin")}</FormLabel>
            <Controller
              control={control}
              name={"linkedIn"}
              defaultValue={props.company.companyContext?.linkedIn}
              render={({ field: { onChange, value } }) => <TextField {...register("linkedIn")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ flex: "0.5" }}>
            <FormLabel>{t("profile.companyInformation.fields.twitter")}</FormLabel>
            <Controller
              control={control}
              name={"twitter"}
              defaultValue={props.company.companyContext?.twitter}
              render={({ field: { onChange, value } }) => <TextField {...register("twitter")} onChange={onChange} value={value} />}
            />
          </CommonFormControl>
        </InformationRowContainer>
        <InformationRowContainer gap={"inherit"}>
          <CommonFormControl sx={{ flex: "1" }}>
            <FormLabel>{t("profile.companyInformation.fields.industry")}</FormLabel>
            <Controller
              control={control}
              name={"industry"}
              defaultValue={props.company.companyContext?.industry}
              render={({ field: { onChange, value } }) => (
                <CommonStyledSelect
                  {...register("industry")}
                  IconComponent={ExpandMore}
                  value={value}
                  MenuProps={CommonStyledSelectMenuProps}
                  onChange={(e) => {
                    if (e.target.value == "Others") {
                      setOtherIndustry(true);
                    } else {
                      setOtherIndustry(false);
                    }
                    onChange(e);
                  }}
                >
                  {INDUSTRIES.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </CommonStyledSelect>
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
        {getValues("industry") == "Others" && otherIndustry && (
          <InformationRowContainer gap={"inherit"}>
            <CommonFormControl sx={{ flex: "1" }}>
              <FormLabel>{t("profile.companyInformation.fields.otherIndustryOption")}</FormLabel>
              <Controller
                control={control}
                name={"industry"}
                defaultValue=""
                render={({ field: { onChange, value } }) => <TextField {...register("industry")} onChange={onChange} value={value} />}
              />
            </CommonFormControl>
          </InformationRowContainer>
        )}
        <InformationRowContainer gap={"inherit"}>
          <CommonFormControl sx={{ flex: "1" }}>
            <FormLabel>{t("profile.companyInformation.fields.shortCompanyDescription")}</FormLabel>
            <Controller
              control={control}
              name={"companyDescription"}
              defaultValue={props.company.companyContext?.companyDescription}
              render={({ field: { onChange, value } }) => (
                <TextField {...register("companyDescription")} onChange={onChange} value={value} />
              )}
            />
          </CommonFormControl>
        </InformationRowContainer>
      </InformationFormContainer>
    </LazoModal>
  );
}

export default CompanyEditForm;
